import type { BenefitResponse, CareerResponse, DailyCompanion, GrantResponse, InvestorScopeMessage, InvestorScopeResponse, JourneyAnswers, PlaceLens, ReportResult, SearchResult, StudyMessage, StudyResponse, TravelMessage, TravelResponse, UncleChatMessage } from './types';

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `request failed: ${res.status}`);
  }
  return data as T;
}

export async function search(query: string, type = 'all'): Promise<SearchResult[]> {
  const params = new URLSearchParams({ q: query, type });
  return parse<{ results: SearchResult[] }>(await fetch(`/api/search?${params}`)).then((r) => r.results);
}

export async function placeLens(query: string): Promise<PlaceLens> {
  const params = new URLSearchParams({ q: query });
  return parse<{ lens: PlaceLens }>(await fetch(`/api/place-lens?${params}`)).then((r) => r.lens);
}

export async function travelChat(messages: TravelMessage[], adjustments?: Record<string, number>): Promise<TravelResponse> {
  return parse<TravelResponse>(await fetch('/api/travel-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, adjustments })
  }));
}

export async function studiesAbroad(messages: StudyMessage[]): Promise<StudyResponse> {
  return parse<StudyResponse>(await fetch('/api/studies-abroad', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages })
  }));
}

export async function dailyCompanion(origins: string): Promise<DailyCompanion> {
  const params = new URLSearchParams({ origins });
  return parse<{ companion: DailyCompanion }>(await fetch(`/api/daily-companion?${params}`)).then((r) => r.companion);
}

export async function investorScope(messages: InvestorScopeMessage[]): Promise<InvestorScopeResponse> {
  return parse<InvestorScopeResponse>(await fetch('/api/investorscope', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode: 'investor', messages })
  }));
}

export async function honestGrants(messages: UncleChatMessage[]): Promise<GrantResponse> {
  return parse<GrantResponse>(await fetch('/api/grants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages })
  }));
}

export async function honestBenefits(messages: UncleChatMessage[]): Promise<BenefitResponse> {
  return parse<BenefitResponse>(await fetch('/api/benefits', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages })
  }));
}

export async function honestCareers(messages: UncleChatMessage[]): Promise<CareerResponse> {
  return parse<CareerResponse>(await fetch('/api/careers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages })
  }));
}

export async function speechAudio(text: string): Promise<Blob> {
  const res = await fetch('/api/speech', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null) as { error?: string; message?: string } | null;
    throw new Error(data?.error || data?.message || `speech failed: ${res.status}`);
  }
  return res.blob();
}

export async function supportCheckout(): Promise<{ url: string }> {
  return parse<{ url: string }>(await fetch('/api/support-checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  }));
}

export async function homeRecommendations(city: string, interests: string[]): Promise<SearchResult[]> {
  const params = new URLSearchParams({ city, interests: interests.join(',') });
  return parse<{ results: SearchResult[] }>(await fetch(`/api/home-recommendations?${params}`)).then((r) => r.results);
}

export async function createReport(answers: JourneyAnswers): Promise<ReportResult> {
  return parse<ReportResult>(await fetch('/api/reports/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(answers)
  }));
}

export async function emailReport(reportId: string, downloadKey: string, email: string): Promise<{ ok: boolean; emailSent: boolean }> {
  return parse(await fetch(`/api/reports/${reportId}/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ downloadKey, email })
  }));
}

export async function adminFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  return parse<T>(await fetch(`/api/admin${path}`, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }));
}
